import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { analyticsService } from '../../utils/analytics';
import { logger } from '../../utils/logger';
import styles from './CustomizableDashboard.module.css';
import Button from '../layout/buttons/Button';
import SummaryStats from '../charts/SummaryStats';
import SimpleBarChart from '../charts/SimpleBarChart';
import EmotionChart from '../charts/EmotionChart';

// Widget components
const WidgetRenderer = ({ widget, data, onEdit, onRemove }) => {
  const renderWidgetContent = () => {
    switch (widget.type) {
      case 'summary-stats':
        return <SummaryStats summary={data.summary} />;
      
      case 'interviews-chart':
        return (
          <SimpleBarChart
            title={widget.title || 'Interviews per Canvass'}
            data={data.interviews || []}
            maxValue={Math.max(...(data.interviews?.map(item => item.value) || [1]))}
          />
        );
      
      case 'completion-rates-chart':
        return (
          <SimpleBarChart
            title={widget.title || 'Completion Rates'}
            data={data.completionRates || []}
            maxValue={100}
          />
        );
      
      case 'emotions-chart':
        return (
          <EmotionChart
            title={widget.title || 'Top Emotions'}
            emotions={data.emotions || []}
          />
        );
      
      case 'metrics-overview':
        return (
          <div className={styles.metricsOverview}>
            <h4>{widget.title || 'Metrics Overview'}</h4>
            <div className={styles.metricsGrid}>
              <div className={styles.metric}>
                <span className={styles.metricValue}>{data.metrics?.totalCanvasses || 0}</span>
                <span className={styles.metricLabel}>Total Canvasses</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.metricValue}>{data.metrics?.totalInterviews || 0}</span>
                <span className={styles.metricLabel}>Total Interviews</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.metricValue}>{data.metrics?.totalCompanies || 0}</span>
                <span className={styles.metricLabel}>Companies</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.metricValue}>{data.metrics?.totalStakeholders || 0}</span>
                <span className={styles.metricLabel}>Stakeholders</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.metricValue}>
                  {Math.round(data.metrics?.averageCompletionRate || 0)}%
                </span>
                <span className={styles.metricLabel}>Avg Completion</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.metricValue}>{data.metrics?.activeCanvasses || 0}</span>
                <span className={styles.metricLabel}>Active Canvasses</span>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className={styles.placeholderWidget}>
            <p>Unknown widget type: {widget.type}</p>
          </div>
        );
    }
  };

  return (
    <div 
      className={`${styles.widget} ${!widget.isVisible ? styles.hidden : ''}`}
      style={{
        gridColumn: `span ${widget.position?.width || 4}`,
        gridRow: `span ${widget.position?.height || 3}`,
      }}
    >
      <div className={styles.widgetHeader}>
        <h3>{widget.title}</h3>
        <div className={styles.widgetActions}>
          {onEdit && (
            <Button variant="outline" size="sm" onClick={() => onEdit(widget)}>
              Edit
            </Button>
          )}
          {onRemove && (
            <Button variant="outline" size="sm" onClick={() => onRemove(widget.id)}>
              Remove
            </Button>
          )}
        </div>
      </div>
      <div className={styles.widgetContent}>
        {renderWidgetContent()}
      </div>
    </div>
  );
};

// Widget configuration modal
const WidgetConfigModal = ({ isOpen, onClose, widget, onSave, availableCanvasses }) => {
  const [config, setConfig] = useState({
    title: widget?.title || '',
    type: widget?.type || 'summary-stats',
    config: widget?.config || {},
    position: widget?.position || { x: 0, y: 0, width: 4, height: 3 },
  });

  const widgetTypes = [
    { value: 'summary-stats', label: 'Summary Statistics' },
    { value: 'interviews-chart', label: 'Interviews Chart' },
    { value: 'completion-rates-chart', label: 'Completion Rates Chart' },
    { value: 'emotions-chart', label: 'Emotions Chart' },
    { value: 'metrics-overview', label: 'Metrics Overview' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(config);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>{widget ? 'Edit Widget' : 'Add Widget'}</h2>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalContent}>
          <div className={styles.formGroup}>
            <label>Widget Type</label>
            <select
              value={config.type}
              onChange={(e) => setConfig({ ...config, type: e.target.value })}
              disabled={!!widget} // Don't allow changing type for existing widgets
            >
              {widgetTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Title</label>
            <input
              type="text"
              value={config.title}
              onChange={(e) => setConfig({ ...config, title: e.target.value })}
              placeholder="Widget title"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Width</label>
            <select
              value={config.position.width}
              onChange={(e) => setConfig({
                ...config,
                position: { ...config.position, width: parseInt(e.target.value) }
              })}
            >
              <option value={2}>Small (2 columns)</option>
              <option value={4}>Medium (4 columns)</option>
              <option value={6}>Large (6 columns)</option>
              <option value={8}>Extra Large (8 columns)</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Height</label>
            <select
              value={config.position.height}
              onChange={(e) => setConfig({
                ...config,
                position: { ...config.position, height: parseInt(e.target.value) }
              })}
            >
              <option value={2}>Short (2 rows)</option>
              <option value={3}>Medium (3 rows)</option>
              <option value={4}>Tall (4 rows)</option>
              <option value={6}>Extra Tall (6 rows)</option>
            </select>
          </div>

          {config.type === 'interviews-chart' && (
            <div className={styles.formGroup}>
              <label>Canvass Filter</label>
              <select
                value={config.config.canvassIds || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, canvassIds: e.target.value }
                })}
              >
                <option value="">All Canvasses</option>
                {availableCanvasses?.map(canvass => (
                  <option key={canvass.id} value={canvass.id}>
                    {canvass.canvassName || canvass.objective}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={styles.modalActions}>
            <Button variant="primary" type="submit">
              {widget ? 'Update' : 'Add'} Widget
            </Button>
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CustomizableDashboard = () => {
  const { accounts } = useMsal();
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [widgetData, setWidgetData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [availableCanvasses, setAvailableCanvasses] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');

  const userId = accounts[0]?.localAccountId || 'anonymous';

  useEffect(() => {
    loadDashboard();
  }, [userId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setConnectionStatus('connecting');
      
      // Load dashboard configuration
      let config;
      try {
        config = await analyticsService.getDashboardConfig(userId);
        setConnectionStatus('connected');
      } catch (configErr) {
        logger.warn('No existing dashboard config, creating default:', configErr);
        setConnectionStatus('connected');
        // Create default dashboard config
        config = {
          userId,
          widgets: [
            {
              id: 'default-metrics',
              type: 'metrics-overview',
              title: 'Overview',
              position: { x: 0, y: 0, width: 6, height: 3 },
              isVisible: true,
              order: 1
            },
            {
              id: 'default-interviews',
              type: 'interviews-chart',
              title: 'Recent Interviews',
              position: { x: 6, y: 0, width: 6, height: 3 },
              isVisible: true,
              order: 2
            }
          ],
          layout: 'grid'
        };
        // Save the default config
        await analyticsService.updateDashboardConfig(userId, config.widgets, config.layout);
      }
      
      setDashboardConfig(config);

      // Load available canvasses for widget configuration
      try {
        const canvasses = await analyticsService.getCanvasses(userId, { limit: 50 });
        setAvailableCanvasses(canvasses);
      } catch (canvassErr) {
        logger.warn('Failed to load canvasses:', canvassErr);
        setAvailableCanvasses([]);
      }

      // Load data for all widgets
      if (config.widgets && config.widgets.length > 0) {
        await loadWidgetData(config.widgets);
      }
      
    } catch (err) {
      logger.error('Error loading dashboard:', err);
      setConnectionStatus('disconnected');
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadWidgetData = async (widgets) => {
    const data = {};

    for (const widget of widgets) {
      try {
        switch (widget.type) {
          case 'summary-stats':
            try {
              const metrics = await analyticsService.getAnalyticsMetrics(userId);
              data[widget.id] = { summary: metrics };
            } catch (metricsErr) {
              logger.warn(`Failed to load metrics for widget ${widget.id}:`, metricsErr);
              data[widget.id] = { 
                summary: {
                  totalCanvasses: 0,
                  totalInterviews: 0,
                  totalCompanies: 0,
                  totalStakeholders: 0,
                  averageCompletionRate: 0,
                  activeCanvasses: 0
                }
              };
            }
            break;

          case 'interviews-chart':
            try {
              const canvassIds = widget.config?.canvassIds 
                ? [widget.config.canvassIds] 
                : undefined;
              const interviews = await analyticsService.getCanvasses(userId, {
                ...(canvassIds && { canvassIds }),
                limit: 10
              });
              data[widget.id] = {
                interviews: interviews.map(canvass => ({
                  name: canvass.canvassName || canvass.objective || 'Unknown',
                  value: canvass.interviewCount || 0,
                  color: 'var(--brand-as-blue-700)',
                  canvassId: canvass.canvassId || canvass.id
                }))
              };
            } catch (interviewsErr) {
              logger.warn(`Failed to load interviews for widget ${widget.id}:`, interviewsErr);
              data[widget.id] = { interviews: [] };
            }
            break;

          case 'completion-rates-chart':
            try {
              const completionRates = await analyticsService.getCompletionRates(userId);
              data[widget.id] = {
                completionRates: completionRates.map(rate => ({
                  name: rate.canvassName || 'Unknown',
                  value: Math.round(rate.rate || 0),
                  color: rate.rate >= 80 ? 'var(--brand-as-green-700)' :
                         rate.rate >= 60 ? 'var(--brand-as-purple-700)' : 'var(--system-amber-600)',
                  canvassId: rate.canvassId
                }))
              };
            } catch (ratesErr) {
              logger.warn(`Failed to load completion rates for widget ${widget.id}:`, ratesErr);
              data[widget.id] = { completionRates: [] };
            }
            break;

          case 'emotions-chart':
            try {
              const emotions = await analyticsService.getTopEmotions(userId);
              data[widget.id] = { emotions: emotions || [] };
            } catch (emotionsErr) {
              logger.warn(`Failed to load emotions for widget ${widget.id}:`, emotionsErr);
              data[widget.id] = { emotions: [] };
            }
            break;

          case 'metrics-overview':
            try {
              const metricsData = await analyticsService.getAnalyticsMetrics(userId);
              data[widget.id] = { metrics: metricsData };
            } catch (metricsOverviewErr) {
              logger.warn(`Failed to load metrics overview for widget ${widget.id}:`, metricsOverviewErr);
              data[widget.id] = { 
                metrics: {
                  totalCanvasses: 0,
                  totalInterviews: 0,
                  totalCompanies: 0,
                  totalStakeholders: 0,
                  averageCompletionRate: 0,
                  activeCanvasses: 0
                }
              };
            }
            break;

          default:
            logger.warn(`Unknown widget type: ${widget.type}`);
            data[widget.id] = {};
        }
      } catch (err) {
        logger.error(`Error loading data for widget ${widget.id}:`, err);
        data[widget.id] = {};
      }
    }

    setWidgetData(data);
  };

  const handleSaveWidget = async (widgetConfig) => {
    try {
      const currentConfig = dashboardConfig || { widgets: [], layout: 'grid' };
      
      if (editingWidget) {
        // Update existing widget
        const updatedWidgets = currentConfig.widgets.map(w =>
          w.id === editingWidget.id
            ? { ...w, ...widgetConfig }
            : w
        );

        await analyticsService.updateDashboardConfig(userId, updatedWidgets, currentConfig.layout);
        setDashboardConfig({ ...currentConfig, widgets: updatedWidgets });
      } else {
        // Add new widget
        const newWidget = await analyticsService.addWidget(
          userId,
          widgetConfig.type,
          widgetConfig.title,
          widgetConfig.config,
          widgetConfig.position
        );

        const updatedWidgets = [...currentConfig.widgets, newWidget];
        await analyticsService.updateDashboardConfig(userId, updatedWidgets, currentConfig.layout);
        setDashboardConfig({ ...currentConfig, widgets: updatedWidgets });

        // Load data for the new widget
        await loadWidgetData([newWidget]);
      }

      setEditingWidget(null);
      setShowWidgetModal(false);
    } catch (err) {
      logger.error('Error saving widget:', err);
      setError('Failed to save widget');
    }
  };

  const handleRemoveWidget = async (widgetId) => {
    try {
      const currentConfig = dashboardConfig || { widgets: [], layout: 'grid' };
      
      await analyticsService.removeWidget(userId, widgetId);
      
      const updatedWidgets = currentConfig.widgets.filter(w => w.id !== widgetId);
      await analyticsService.updateDashboardConfig(userId, updatedWidgets, currentConfig.layout);
      setDashboardConfig({ ...currentConfig, widgets: updatedWidgets });

      // Remove widget data
      const newData = { ...widgetData };
      delete newData[widgetId];
      setWidgetData(newData);
    } catch (err) {
      logger.error('Error removing widget:', err);
      setError('Failed to remove widget');
    }
  };

  const handleEditWidget = (widget) => {
    setEditingWidget(widget);
    setShowWidgetModal(true);
  };

  const handleAddWidget = () => {
    setEditingWidget(null);
    setShowWidgetModal(true);
  };

  const handleRefresh = () => {
    loadDashboard();
  };

  if (loading) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
          <p>Loading your customizable dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={handleRefresh} variant="primary">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <div className={styles.dashboardTitle}>
          <h2>{accounts[0]?.name || 'User'}'s Customizable Dashboard</h2>
          <div className={`${styles.connectionStatus} ${styles[connectionStatus]}`}>
            <span className={styles.statusDot}></span>
            <span className={styles.statusText}>
              {connectionStatus === 'connected' ? 'Analytics Connected' : 
               connectionStatus === 'connecting' ? 'Connecting...' : 'Connection Lost'}
            </span>
          </div>
        </div>
        <p>Personalize your dashboard with the widgets that matter most to you</p>
        <div className={styles.dashboardActions}>
          <Button 
            variant={isEditMode ? "primary" : "outline-neutral"}
            onClick={() => setIsEditMode(!isEditMode)}
          >
            {isEditMode ? 'Done Editing' : 'Edit Layout'}
          </Button>
          <Button variant="outline-neutral" onClick={handleRefresh}>
            Refresh
          </Button>
          {isEditMode && (
            <Button variant="primary" onClick={handleAddWidget}>
              Add Widget
            </Button>
          )}
        </div>
      </div>

      <div className={styles.widgetsGrid}>
        {dashboardConfig?.widgets
          ?.filter(widget => widget.isVisible)
          ?.sort((a, b) => a.order - b.order)
          ?.map(widget => (
            <WidgetRenderer
              key={widget.id}
              widget={widget}
              data={widgetData[widget.id] || {}}
              onEdit={isEditMode ? handleEditWidget : undefined}
              onRemove={isEditMode ? handleRemoveWidget : undefined}
            />
          )) || (
          <div className={styles.emptyState}>
            <p>No widgets configured. Add your first widget to get started!</p>
          </div>
        )}
      </div>

      <WidgetConfigModal
        isOpen={showWidgetModal}
        onClose={() => {
          setShowWidgetModal(false);
          setEditingWidget(null);
        }}
        widget={editingWidget}
        onSave={handleSaveWidget}
        availableCanvasses={availableCanvasses}
      />
    </div>
  );
};

export default CustomizableDashboard;
